import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { useLocaleFormatter } from '@casops/shared/hooks/useLocaleFormatter';
import { useAppSelector } from '@casops/store/hooks';
import { DateTime } from 'luxon';

// 48 hours worth of 15-minute intervals
const allIntervals = Array.from({ length: 48 * 4 }, (_, i) => i * 15);

interface UseStartTimesReturn {
  allStartTimes: [string, string][];
  uniqueStartDays: string[];
}

export const useStartTimes = (): UseStartTimesReturn => {
  const { timezone } = useAppSelector(state => state.settings.localization);

  const currentTime = DateTime.now().setZone(timezone);

  const nearestQuarterHour = currentTime.set({
    minute: currentTime.minute - (currentTime.minute % 15),
    second: 0,
    millisecond: 0,
  });

  const allStartTimes: [string, string][] = allIntervals.map(minutes => {
    const startTime = nearestQuarterHour.minus({ minutes });
    return [startTime.startOf('day').toString(), startTime.toString()];
  });

  const uniqueStartDays: string[] = [...new Set(allStartTimes.map(([day]) => day.toString()))];

  return {
    allStartTimes,
    uniqueStartDays,
  };
};

export const useStartDayOptions = (uniqueStartDays: string[]): DropdownItemProps<string>[] => {
  const { showDateOnlyFromIso } = useLocaleFormatter();

  const startDayOptions: DropdownItemProps<string>[] = uniqueStartDays.map(startDay => {
    const stringValue = startDay.toString();
    return {
      key: stringValue,
      value: stringValue,
      stringValue,
      label: showDateOnlyFromIso(startDay),
    };
  });

  return startDayOptions;
};

export const useStartDateTimeOptions = (
  allStartTimes: [string, string][],
): DropdownItemProps<string>[] => {
  const { showTimeOnlyFromIso } = useLocaleFormatter();

  const startDateTimeOptions: DropdownItemProps<string>[] = allStartTimes.map(
    ([, startDateTime]: [string, string]) => {
      return {
        key: startDateTime,
        value: startDateTime,
        stringValue: startDateTime,
        label: showTimeOnlyFromIso(startDateTime, true),
      };
    },
  );

  return startDateTimeOptions;
};
